import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { usePageTracking } from "@/hooks/usePageTracking";
import LandingPage from "@/pages/LandingPage";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import FAQsPage from "@/pages/FAQsPage";
import SupportPage from "@/pages/SupportPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import Dashboard from "@/pages/Dashboard";
import EventDetail from "@/pages/EventDetail";
import WishlistDetail from "@/pages/WishlistDetail";
import PublicEventView from "@/pages/PublicEventView";
import PublicInvitationView from "@/pages/PublicInvitationView";
import AdminDashboard from "@/pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function AuthHandler({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuth = async () => {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          }, { withCredentials: true });
          
          setUser(response.data.user);
          setLoading(false);
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/home');
        } catch (error) {
          console.error('Auth error:', error);
          toast.error('Authentication failed');
          setLoading(false);
        }
        return;
      }

      // Check existing session
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    handleAuth();
  }, [navigate]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

// Page tracking component
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageTracker />
        <AuthHandler>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faqs" element={<FAQsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/events/:eventId" element={
              <ProtectedRoute>
                <EventDetail />
              </ProtectedRoute>
            } />
            <Route path="/wishlists/:wishlistId" element={
              <ProtectedRoute>
                <WishlistDetail />
              </ProtectedRoute>
            } />
            <Route path="/e/:eventId" element={<PublicEventView />} />
            <Route path="/i/:eventId" element={<PublicInvitationView />} />
          </Routes>
        </AuthHandler>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;